import React, { useEffect, useState } from 'react';
import {
  createDeposit, updateDeposit, changeStateDeposit, changeMainDeposit, deleteDeposit,
} from '../services/depositService';

const EMPTY_FORM = { name: '' };

export default function DepositManager({ branchId, deposits, onChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(EMPTY_FORM);
    setEditingDeposit(null);
    setPendingDelete(null);
    setError('');
  }, [branchId]);

  function selectDeposit(deposit) {
    setError('');
    setPendingDelete(null);
    if (editingDeposit?.id === deposit.id) {
      setEditingDeposit(null);
      setForm(EMPTY_FORM);
    } else {
      setEditingDeposit(deposit);
      setForm({ name: deposit.name || '' });
    }
  }

  function cancelEdit() {
    setEditingDeposit(null);
    setForm(EMPTY_FORM);
    setPendingDelete(null);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    const payload = { name: form.name.trim(), branchId };

    try {
      if (editingDeposit) {
        await updateDeposit(editingDeposit.id, payload);
      } else {
        await createDeposit(payload);
      }
      cancelEdit();
      await onChange();
    } catch (err) {
      const msg = err.response?.data?.msg || 'No se pudo guardar el depósito.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function toggleState(deposit) {
    setError('');
    setBusy(true);
    try {
      await changeStateDeposit(deposit.id);
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo cambiar el estado.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleMain(deposit) {
    setError('');
    setBusy(true);
    try {
      await changeMainDeposit(deposit.id);
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo establecer el depósito principal.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setError('');
    setBusy(true);
    try {
      await deleteDeposit(pendingDelete.id);
      cancelEdit();
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo eliminar el depósito.');
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error && (
        <div className="alert-orig alert-error" style={{ marginBottom: 14 }}>
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ marginBottom: 14 }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-8">
            <label className="form-label">Nombre del depósito</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ej: Deposito Principal"
              value={form.name}
              onChange={e => setForm({ name: e.target.value })}
              required
            />
          </div>
          <div className="col-12 col-md-4" style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn-orig btn-orig-green" disabled={saving || busy}>
              <i className="bi bi-check-lg" />
              {editingDeposit ? 'Actualizar' : 'Agregar'}
            </button>
            {editingDeposit && (
              <button type="button" className="btn-orig btn-orig-outline" onClick={cancelEdit} disabled={saving || busy}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </form>

      {deposits.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--orig-text-muted)', fontSize: 13 }}>
          No hay depósitos registrados
        </div>
      ) : (
        <div style={{ border: '1px solid var(--orig-border)', borderRadius: 'var(--orig-radius)', overflow: 'hidden' }}>
          {deposits.map((deposit, idx) => (
            <div key={deposit.id}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  padding: '10px 14px',
                  borderBottom: idx < deposits.length - 1 || pendingDelete?.id === deposit.id ? '1px solid var(--orig-border)' : 'none',
                  background: editingDeposit?.id === deposit.id ? 'var(--orig-green-dim)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--orig-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 120 }}>
                    {deposit.name}
                  </span>
                  {deposit.main && (
                    <span className="badge-orig badge-green" title="Depósito principal">
                      <i className="bi bi-star-fill" style={{ fontSize: 9 }} />
                      Principal
                    </span>
                  )}
                  <span className={`badge-orig ${deposit.state ? 'badge-green' : 'badge-red'}`}>
                    {deposit.state ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {!deposit.main && (
                    <button
                      type="button"
                      className="btn-orig btn-orig-outline"
                      onClick={() => toggleMain(deposit)}
                      disabled={busy}
                      style={{ padding: '4px 10px', fontSize: 11.5 }}
                      title="Marcar como principal"
                    >
                      <i className="bi bi-star" />
                      Principal
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-orig btn-orig-outline"
                    onClick={() => toggleState(deposit)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 11.5 }}
                    title="Cambiar estado"
                  >
                    <i className="bi bi-toggle2-on" />
                  </button>
                  <button
                    type="button"
                    className="btn-orig btn-orig-outline"
                    onClick={() => selectDeposit(deposit)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 11.5 }}
                    title="Editar depósito"
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn-orig btn-orig-outline"
                    onClick={() => setPendingDelete(deposit)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 11.5, color: 'var(--orig-red-light)' }}
                    title="Eliminar depósito"
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
              </div>
              {pendingDelete?.id === deposit.id && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  background: 'var(--orig-red-dim)',
                  borderBottom: idx < deposits.length - 1 ? '1px solid var(--orig-border)' : 'none',
                  padding: '10px 14px',
                }}>
                  <span style={{ fontSize: 13, color: 'var(--orig-text)' }}>
                    ¿Eliminar <strong>{pendingDelete.name}</strong>?
                  </span>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                    <button type="button" className="btn-orig btn-orig-green" onClick={confirmDelete} disabled={busy}>
                      Sí, eliminar
                    </button>
                    <button type="button" className="btn-orig btn-orig-outline" onClick={() => setPendingDelete(null)} disabled={busy}>
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
